import { useState, useMemo } from "react";
import { where } from "firebase/firestore";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { relativeDate } from "@/lib/humanDate";
import { fromTimestamp } from "@/lib/firestore";
import { CreateChatModal } from "@/components/features/CreateChatModal";
import type { HouseholdChat } from "@/lib/models";
import { Link } from "react-router-dom";
import { useTranslations } from "@/contexts/I18nContext";

/** Returns true if this chat has unread messages for the given user */
function isChatUnread(chat: HouseholdChat, userId: string): boolean {
  // Not unread if the current user sent the last reply
  if (chat.last_reply_uid === userId) return false;
  // Not unread if there's no last reply
  if (!chat.last_reply_uid) return false;
  // Unread if last_reply_read_by doesn't include the user
  const readBy = chat.last_reply_read_by ?? [];
  return !readBy.includes(userId);
}

export function ChatsPage() {
  const { householdId, members, profile, loading } = useHouseholdContext();
  const t = useTranslations();
  const [showCreate, setShowCreate] = useState(false);

  // Use a Firestore array-contains query to fetch only chats the current
  // user is a member of — matches iOS (LocalDataStore.swift:86) and Android
  // (ZoeyRepository.kt:504), which both use whereArrayContains("chat_members",
  // userId). Server-side filter is more efficient than fetching all chats
  // and filtering on the client, and aligns with the tightened Firestore
  // rule that only allows chat members to read.
  const { data: chats, loading: chatsLoading } =
    useFirestoreCollection<HouseholdChat>(
      householdId && profile?.uid ? `households/${householdId}/chats` : null,
      profile?.uid ? [where("chat_members", "array-contains", profile.uid)] : [],
    );

  // Sort by most-recent-activity desc and dedupe 1-on-1 chats by the
  // sorted member-id pair — older clients sometimes wrote multiple
  // chat docs for the same pair (an iOS bug fixed in 2025-Q4 but the
  // legacy duplicates persist). For groups (3+ members) we keep all
  // chats; only 1-on-1s collapse.
  const sortedChats = useMemo(() => {
    const sorted = [...chats].sort((a, b) => {
      const aTime =
        fromTimestamp(a.last_reply_at)?.getTime() ??
        fromTimestamp(a.created_at)?.getTime() ??
        0;
      const bTime =
        fromTimestamp(b.last_reply_at)?.getTime() ??
        fromTimestamp(b.created_at)?.getTime() ??
        0;
      return bTime - aTime;
    });
    const seenPairs = new Set<string>();
    const out: HouseholdChat[] = [];
    for (const c of sorted) {
      if (c.chat_members.length === 2) {
        const key = [...c.chat_members].sort().join("|");
        if (seenPairs.has(key)) continue;
        seenPairs.add(key);
      }
      out.push(c);
    }
    return out;
  }, [chats]);

  const findMember = (uid: string) => members.find((m) => m.uid === uid);

  if (loading || chatsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("chats.title")}</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 rounded-lg bg-[color:var(--accent-color)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          {t("chats.newChat")}
        </button>
      </div>

      {sortedChats.length === 0 ? (
        <Card>
          <EmptyState
            icon="💬"
            title={t("chats.empty.title")}
            subtitle={t("chats.empty.subtitle")}
            buttonLabel={t("chats.newChat")}
            onButtonClick={() => setShowCreate(true)}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {sortedChats.map((chat) => {
            const lastAuthor = chat.last_reply_uid
              ? findMember(chat.last_reply_uid)
              : findMember(chat.created_by);
            const date = fromTimestamp(
              chat.last_reply_at ?? chat.created_at
            );
            const previewText = chat.last_reply_text ?? chat.text;
            // For 1-on-1 chats, show the *other* participant's name +
            // avatar — not the current user's. Mirrors iOS
            // `ChatView.swift:32-38 chatAvatarProfile()`.
            const isOneOnOne = chat.chat_members.length === 2;
            const otherMemberIds = profile?.uid
              ? chat.chat_members.filter((id) => id !== profile.uid)
              : chat.chat_members;
            const displayMemberIds = isOneOnOne
              ? otherMemberIds
              : chat.chat_members;
            const displayName = isOneOnOne
              ? (() => {
                  const other = findMember(otherMemberIds[0] ?? "");
                  return other?.nickname ?? other?.name ?? "";
                })()
              : chat.chat_members
                  .map((id) => {
                    const m = findMember(id);
                    return m?.nickname ?? m?.name ?? "";
                  })
                  .filter(Boolean)
                  .join(", ");
            const unread = profile?.uid
              ? isChatUnread(chat, profile.uid)
              : false;

            return (
              <Link
                key={chat.uid}
                to={`/chats/${chat.uid}`}
                className="block"
              >
                <Card>
                  <div className="flex items-center gap-3">
                    {/* Member avatars — for 1-on-1, show only the
                        other participant. */}
                    <div className="flex -space-x-2 shrink-0">
                      {displayMemberIds.slice(0, 3).map((memberId) => {
                        const m = findMember(memberId);
                        return (
                          <Avatar
                            key={memberId}
                            src={m?.profile_url}
                            alt={m?.nickname ?? m?.name ?? ""}
                            size={36}
                            className="ring-2 ring-white dark:ring-gray-900"
                          />
                        );
                      })}
                      {displayMemberIds.length > 3 && (
                        <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 ring-2 ring-white dark:ring-gray-900">
                          +{displayMemberIds.length - 3}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p
                          className={`text-sm truncate ${unread ? "font-bold" : "font-semibold"}`}
                        >
                          {displayName || "Chat"}
                        </p>
                        <div className="flex items-center gap-2 ml-2 shrink-0">
                          {date && (
                            <span className="text-xs text-gray-400">
                              {relativeDate(date)}
                            </span>
                          )}
                          {unread && (
                            <span
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{
                                backgroundColor: "var(--accent-color, #00C7BE)",
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <p
                        className={`text-sm mt-0.5 line-clamp-1 ${unread ? "font-semibold text-gray-700 dark:text-gray-200" : "text-gray-500"}`}
                      >
                        {lastAuthor
                          ? `${lastAuthor.nickname ?? lastAuthor.name ?? ""}: `
                          : ""}
                        {previewText}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateChatModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}
